// Geometry: limit lines (implicit form A·x + B·y = C, stable for every θ), oblique screw grid, maximum grid,
// spreading inside the admissible zone, grid steps.
// θ reference: θ = 0° → F (and element 1 axis) pointing DOWN; θ = 90° element 1 horizontal; counter-clockwise.

import { D2R } from "./angles";
import type { EngineTexts } from "./ctx";
import { ceilMm, type Spacing } from "./spacing";

export function elem1Trig(thetaDeg: number): { c: number; s: number } {
  const th = thetaDeg * D2R;
  return { c: -Math.cos(th), s: -Math.sin(th) };
}

export type Line = {
  name: string;
  A: number;
  B: number;
  C: number;
  side: number; // +1: A·x+B·y >= C admissible, -1: <= C
  signedValue(x: number, y: number): number;
  isAdmissible(x: number, y: number): boolean;
  endpoints(half: number): [number, number, number, number];
};

// The admissible side is FIXED by the role of the line (`side`), not deduced from the sign of C (that degenerates
// when C = 0, i.e. zero margin, where the constraint must stay fully active).
export function makeLine(A: number, B: number, C: number, side: number, name: string): Line {
  const norm = Math.sqrt(A * A + B * B) || 1;
  return {
    name,
    A,
    B,
    C,
    side,
    signedValue(x: number, y: number) {
      return A * x + B * y - C;
    },
    isAdmissible(x: number, y: number) {
      const s = this.signedValue(x, y);
      return side > 0 ? s >= -1e-9 : s <= 1e-9;
    },
    endpoints(half: number) {
      const p0x = (A * C) / (A * A + B * B),
        p0y = (B * C) / (A * A + B * B);
      const dx = -B / norm,
        dy = A / norm;
      const t = half * 2.2;
      return [p0x - dx * t, p0y - dy * t, p0x + dx * t, p0y + dy * t];
    },
  };
}

export type Element1Lines = { retta1: Line; rettaN: Line; retta3: Line; continuo1: boolean };
export type Element2Bounds = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  limiteInferioreTipo: "a3t2" | "a3c2";
  continuo2: boolean;
};

// continuo1: element 1 has no real end on the side opposite to F → retta 3 is the VIRTUAL end b2/(2·|cos θ|)
// measured from the axes crossing, without subtracting a3,t,1.
export function element1Lines(
  thetaDeg: number,
  b1: number,
  d1: number,
  a4t1: number,
  a4c1: number,
  a3t1: number,
  continuo1: boolean,
  b2: number,
): Element1Lines {
  const { c, s } = elem1Trig(thetaDeg);
  const estremita1 = continuo1 ? b2 / 2 / Math.abs(c) : d1 - a3t1;
  return {
    retta1: makeLine(c, s, b1 / 2 - a4t1, -1, "retta 1"),
    rettaN: makeLine(c, s, -(b1 / 2 - a4c1), 1, "retta n"),
    retta3: makeLine(-s, c, -estremita1, 1, "retta 3"),
    continuo1: !!continuo1,
  };
}
// Element 2 is infinite upwards: the only real end is the lower one (a3,t,2 for 0°<θ<=90°, a3,c,2 for 90°<θ<=180°).
// continuo2: virtual end -(b1/(2·|cos θ|)).
export function element2Bounds(
  b2: number,
  d2: number,
  a4t2: number,
  a4c2: number,
  a3t2: number,
  a3c2: number | null | undefined,
  thetaDeg: number,
  continuo2: boolean,
  b1: number,
): Element2Bounds {
  const a3cResolved = a3c2 != null && !Number.isNaN(a3c2) ? a3c2 : a3t2;
  const limiteInferioreTipo = thetaDeg <= 90 ? "a3t2" : "a3c2";
  const a3Basso = thetaDeg <= 90 ? a3t2 : a3cResolved;
  const cosTheta = Math.cos((thetaDeg * Math.PI) / 180);
  const estremita2 = continuo2 ? b1 / 2 / Math.abs(cosTheta) : d2 - a3Basso;
  return {
    xMin: -(b2 / 2 - a4c2),
    xMax: b2 / 2 - a4t2,
    yMin: -estremita2,
    yMax: Infinity,
    limiteInferioreTipo,
    continuo2: !!continuo2,
  };
}
export function isPointAdmissible(
  x: number,
  y: number,
  lines1: Element1Lines,
  bounds2: Element2Bounds,
  t: EngineTexts,
): { ok: boolean; violations: string[] } {
  const tv = t.engine.violations;
  const violations: string[] = [];
  if (x < bounds2.xMin) violations.push(tv.edge2("a4,c,2"));
  if (x > bounds2.xMax) violations.push(tv.edge2("a4,t,2"));
  if (y < bounds2.yMin) violations.push(tv.end2(bounds2.limiteInferioreTipo === "a3t2" ? "a3,t,2" : "a3,c,2"));
  if (!lines1.retta1.isAdmissible(x, y)) violations.push(tv.line1("1", "a4,t,1"));
  if (!lines1.rettaN.isAdmissible(x, y)) violations.push(tv.line1("n", "a4,c,1"));
  if (!lines1.retta3.isAdmissible(x, y)) violations.push(tv.line1("3", "a3,t,1"));
  return { ok: violations.length === 0, violations };
}
// Same test as isPointAdmissible(...).ok without allocating the violations (hot loops of the maximum grid search).
export function isPointOk(x: number, y: number, lines1: Element1Lines, bounds2: Element2Bounds): boolean {
  return !(
    x < bounds2.xMin ||
    x > bounds2.xMax ||
    y < bounds2.yMin ||
    !lines1.retta1.isAdmissible(x, y) ||
    !lines1.rettaN.isAdmissible(x, y) ||
    !lines1.retta3.isAdmissible(x, y)
  );
}

// Tolerance (degrees) for θ EXACTLY 0°/180° (coincident axes).
export const GRID_AXIS_PARALLEL_TOL = 1e-9;
export function isThetaParallel(thetaDeg: number): boolean {
  return Math.abs(thetaDeg) < GRID_AXIS_PARALLEL_TOL || Math.abs(thetaDeg - 180) < GRID_AXIS_PARALLEL_TOL;
}
// Direction of the s12 grid axis: normally element 2's axis (0,1); at θ = 0°/180° (or when the "cross" term
// dominates, see passiGrigliaObliqua) perpendicular to e1, to rebuild a rectangular grid.
export function gridAxis2(thetaDeg: number, c: number, s: number, crossDominant: boolean | undefined): { ex: number; ey: number } {
  if (isThetaParallel(thetaDeg) || crossDominant) return { ex: c, ey: s };
  return { ex: 0, ey: 1 };
}

export type GridPointBase = { i: number; j: number; u: number; v: number; x: number; y: number };
export type GridPoint = GridPointBase & { ok: boolean; violations: string[] };

// Oblique lattice P = u0·e1 + w0·e2 (fasteners on lines parallel to BOTH element axes). offsetX/offsetY: rigid
// translation in the fixed drawing plane (x,y). sfalsaViti: every row parallel to the grain staggered orthogonally
// by ±sfalsaD/2 (EC5 Table 8.1 note 8 / fig. 8.6). i = row along e1 (n_ef line), j = column along e2.
export function generateScrewGrid(
  thetaDeg: number,
  rows: number,
  cols: number,
  s11: number,
  s12: number,
  offsetX: number,
  offsetY: number,
  sfalsaViti: boolean,
  sfalsaD: number,
  crossDominant: boolean,
): GridPointBase[] {
  offsetX = offsetX || 0;
  offsetY = offsetY || 0;
  const { c: cT, s: sT } = elem1Trig(thetaDeg);
  const { ex: e2x, ey: e2y } = gridAxis2(thetaDeg, cT, sT, crossDominant);
  const uOff = ((rows - 1) * s11) / 2,
    wOff = ((cols - 1) * s12) / 2;
  const staggerHalf = sfalsaViti && sfalsaD ? sfalsaD / 2 : 0;
  const pts: GridPointBase[] = [];
  for (let i = 0; i < rows; i++) {
    const u0 = i * s11 - uOff;
    const staggerW = i % 2 === 0 ? -staggerHalf : staggerHalf;
    for (let j = 0; j < cols; j++) {
      const w0 = j * s12 - wOff + staggerW;
      const x = -u0 * sT + w0 * e2x + offsetX;
      const y = u0 * cT + w0 * e2y + offsetY;
      const u = -sT * x + cT * y,
        v = cT * x + sT * y;
      pts.push({ i, j, u, v, x, y });
    }
  }
  return pts;
}

// SPREADING IN THE ADMISSIBLE ZONE (user request 2026-10-14): with automatic distances and #spreadViti the chosen
// rows/columns move to the limits of the admissible zone (outer screws at the minimum edge/end distances, the others
// equidistant). In lattice coordinates the grid is the rectangle [uLo,uHi]×[wLo,wHi]: first u is extended (w at the
// minimum step around the current centre), then w; the centre moves to the middle. Steps floored to the whole mm.
// margin = room reserved to staggering (d/2 staggered rows, d staggered sides).
export function spreadGridParams(
  theta: number,
  rows: number,
  cols: number,
  s11Min: number,
  s12Min: number,
  cx: number,
  cy: number,
  lines1: Element1Lines,
  bounds2: Element2Bounds,
  crossDominant: boolean,
  margin: number,
): { s11: number; s12: number; offsetX: number; offsetY: number; spread: boolean } {
  const base = { s11: s11Min, s12: s12Min, offsetX: cx, offsetY: cy, spread: false };
  if (!(rows >= 1 && cols >= 1) || !(s11Min > 0) || !(s12Min > 0) || (rows < 2 && cols < 2)) return base;
  const { c: cT, s: sT } = elem1Trig(theta);
  const { ex: e2x, ey: e2y } = gridAxis2(theta, cT, sT, crossDominant);
  const det = -sT * e2y - e2x * cT;
  if (!(Math.abs(det) > 1e-9)) return base;
  const cons: { Au: number; Aw: number; c: number }[] = [];
  const add = (a: number, b: number, c: number) => {
    const n = Math.hypot(a, b);
    if (!(n > 0) || !Number.isFinite(c)) return;
    a /= n;
    b /= n;
    c = c / n - (margin || 0);
    cons.push({ Au: -a * sT + b * cT, Aw: a * e2x + b * e2y, c });
  };
  if (Number.isFinite(bounds2.xMin)) add(-1, 0, -bounds2.xMin);
  if (Number.isFinite(bounds2.xMax)) add(1, 0, bounds2.xMax);
  if (Number.isFinite(bounds2.yMin)) add(0, -1, -bounds2.yMin);
  if (Number.isFinite(bounds2.yMax)) add(0, 1, bounds2.yMax);
  [lines1.retta1, lines1.rettaN, lines1.retta3].forEach((l) => {
    if (l.side > 0) add(-l.A, -l.B, -l.C);
    else add(l.A, l.B, l.C);
  });
  // axis 'u': w fixed → interval of u; axis 'w': u fixed → interval of w
  const range = (fixedVal: number, axis: "u" | "w"): { lo: number; hi: number } | null => {
    let lo = -Infinity,
      hi = Infinity;
    for (const k of cons) {
      const A = axis === "u" ? k.Au : k.Aw,
        B = axis === "u" ? k.Aw : k.Au;
      const rhs = k.c - B * fixedVal;
      if (Math.abs(A) < 1e-9) {
        if (rhs < -1e-9) return null;
        continue;
      }
      if (A > 0) hi = Math.min(hi, rhs / A);
      else lo = Math.max(lo, rhs / A);
    }
    return { lo, hi };
  };
  const inter = (r1: { lo: number; hi: number } | null, r2: { lo: number; hi: number } | null) =>
    r1 && r2 ? { lo: Math.max(r1.lo, r2.lo), hi: Math.min(r1.hi, r2.hi) } : null;
  let uC = (e2y * cx - e2x * cy) / det,
    wC = (-cT * cx - sT * cy) / det;
  let s11 = s11Min,
    s12 = s12Min,
    spread = false;
  if (rows > 1) {
    const wH = ((cols - 1) * s12Min) / 2;
    const r = inter(range(wC - wH, "u"), range(wC + wH, "u"));
    if (r && Number.isFinite(r.lo) && Number.isFinite(r.hi) && r.hi - r.lo >= (rows - 1) * s11Min - 1e-6) {
      s11 = Math.max(s11Min, Math.floor((r.hi - r.lo) / (rows - 1) + 1e-9));
      uC = (r.lo + r.hi) / 2;
      spread = spread || s11 > s11Min;
    }
  }
  if (cols > 1) {
    const uH = ((rows - 1) * s11) / 2;
    const r = inter(range(uC - uH, "w"), range(uC + uH, "w"));
    if (r && Number.isFinite(r.lo) && Number.isFinite(r.hi) && r.hi - r.lo >= (cols - 1) * s12Min - 1e-6) {
      s12 = Math.max(s12Min, Math.floor((r.hi - r.lo) / (cols - 1) + 1e-9));
      wC = (r.lo + r.hi) / 2;
      spread = spread || s12 > s12Min;
    }
  }
  return { s11, s12, offsetX: -uC * sT + wC * e2x, offsetY: uC * cT + wC * e2y, spread };
}

// Maximum rows × cols respecting the minimum distances of both elements for the steps s11/s12: only the 4 corners of
// each candidate are checked (convex admissible region, linear lattice map). opts.cap: practical cap (500).
// opts.maxCornerDistance: cap on the real distance between the extreme screws (grazing angles), preferring the
// layout with more screws.
export function maxScrewGrid(
  thetaDeg: number,
  lines1: Element1Lines,
  bounds2: Element2Bounds,
  s11: number,
  s12: number,
  opts?: { cap?: number; maxCornerDistance?: number | null; crossDominant?: boolean },
): { rows: number; cols: number; total: number; cornerDistanceCapped: boolean; capped: boolean } {
  const cap = (opts && opts.cap) || 500;
  const maxCornerDistance = opts && opts.maxCornerDistance != null ? opts.maxCornerDistance : null;
  const crossDominant = opts && opts.crossDominant;
  const { c, s } = elem1Trig(thetaDeg);
  const { ex: e2x, ey: e2y } = gridAxis2(thetaDeg, c, s, crossDominant);
  function corners(rows: number, cols: number) {
    const uHalf = ((rows - 1) * s11) / 2,
      wHalf = ((cols - 1) * s12) / 2;
    const us = uHalf === 0 ? [0] : [-uHalf, uHalf];
    const ws = wHalf === 0 ? [0] : [-wHalf, wHalf];
    const pts: { x: number; y: number }[] = [];
    for (const u0 of us) for (const w0 of ws) pts.push({ x: -u0 * s + w0 * e2x, y: u0 * c + w0 * e2y });
    return pts;
  }
  function cornersOk(pts: { x: number; y: number }[]) {
    for (const p of pts) if (!isPointOk(p.x, p.y, lines1, bounds2)) return false;
    return true;
  }
  function maxPairwiseDistance(pts: { x: number; y: number }[]) {
    let maxD = 0;
    pts.forEach((p, i) => {
      for (const q of pts.slice(i + 1)) {
        const d = Math.hypot(p.x - q.x, p.y - q.y);
        if (d > maxD) maxD = d;
      }
    });
    return maxD;
  }
  let best = { rows: 0, cols: 0, total: 0 };
  let bestUnconstrained = { rows: 0, cols: 0, total: 0 };
  // The admissible (rows, cols) form a down-set (a smaller centred parallelogram is a convex combination of the
  // larger one's corners), so the scan stops at the first failure: same result as the source's full 500×500 scan.
  for (let rows = 1; rows <= cap; rows++) {
    let anyCol = false;
    for (let cols = 1; cols <= cap; cols++) {
      const pts = corners(rows, cols);
      if (!cornersOk(pts)) break;
      anyCol = true;
      const total = rows * cols;
      if (total > bestUnconstrained.total) bestUnconstrained = { rows, cols, total };
      if (maxCornerDistance != null && maxPairwiseDistance(pts) > maxCornerDistance) continue;
      if (total > best.total) best = { rows, cols, total };
    }
    if (!anyCol) break;
  }
  return {
    rows: best.rows,
    cols: best.cols,
    total: best.total,
    cornerDistanceCapped: maxCornerDistance != null && best.total < bestUnconstrained.total,
    capped: bestUnconstrained.rows === cap || bestUnconstrained.cols === cap,
  };
}

// The two steps of the oblique lattice (passoAsse1 along element 1's axis, passoAsse2 along element 2's):
// timber:  passoAsse1 = MAX(sp1.a1; sp2.a2/|sin θ'|), passoAsse2 = MAX(sp2.a1; sp1.a2/|sin θ'|), θ' = θ folded acute;
// steel (skipElem1Spacing, a plate has no grain): passoAsse1 = sp2.a2/|sin θ'|, passoAsse2 = sp2.a1;
// θ EXACTLY 0°/180°: dedicated branch without divisions (MAX of the a1 / of the a2);
// "cross" term dominating: s2 perpendicular to element 1 (see gridAxis2): passoAsse1 = a1,2/|cos θ|,
// passoAsse2 = MIN(a2,2/|sin θ|; 1.15·a2,2) — not at θ = 90° (cos ≈ 6e-17 would diverge).
// fi1/fi2 = 90° swap which field (a1 "own axis" / a2 "transverse") is read for each geometric role.
export function passiGrigliaObliqua(
  sp1: Spacing,
  sp2: Spacing,
  thetaDeg: number,
  skipElem1Spacing: boolean | undefined,
  fi1: number,
  fi2: number,
): { passoAsse1: number; passoAsse2: number; crossDominant: boolean } {
  const r = passiGrigliaObliquaRaw(sp1, sp2, thetaDeg, skipElem1Spacing, fi1, fi2);
  return { ...r, passoAsse1: ceilMm(r.passoAsse1), passoAsse2: ceilMm(r.passoAsse2) };
}
export function passiGrigliaObliquaRaw(
  sp1: Spacing,
  sp2: Spacing,
  thetaDeg: number,
  skipElem1Spacing: boolean | undefined,
  fi1: number,
  fi2: number,
): { passoAsse1: number; passoAsse2: number; crossDominant: boolean } {
  fi1 = fi1 || 0;
  fi2 = fi2 || 0;
  const a1Own1 = fi1 === 90 ? sp1.a2 : sp1.a1;
  const a2Cross1 = fi1 === 90 ? sp1.a1 : sp1.a2;
  const a1Own2 = fi2 === 90 ? sp2.a2 : sp2.a1;
  const a2Cross2 = fi2 === 90 ? sp2.a1 : sp2.a2;
  const thetaFold = thetaDeg <= 90 ? thetaDeg : 180 - thetaDeg;
  const parallelo = Math.abs(thetaDeg) < 1e-9 || Math.abs(thetaDeg - 180) < 1e-9;
  if (parallelo) {
    return {
      passoAsse1: skipElem1Spacing ? a1Own2 : Math.max(a1Own1, a1Own2),
      passoAsse2: skipElem1Spacing ? a2Cross2 : Math.max(a2Cross1, a2Cross2),
      crossDominant: false,
    };
  }
  const sinFold = Math.abs(Math.sin(thetaFold * D2R));
  const cosNonNullo = Math.abs(Math.cos(thetaDeg * D2R)) > 1e-6;
  const crossDominant =
    cosNonNullo &&
    (skipElem1Spacing ? a1Own2 < a2Cross1 / sinFold : a1Own1 < a2Cross2 / sinFold || a1Own2 < a2Cross1 / sinFold);
  if (crossDominant) {
    const cosTheta = Math.abs(Math.cos(thetaDeg * D2R));
    const sinTheta = Math.abs(Math.sin(thetaDeg * D2R));
    return {
      passoAsse1: a1Own2 / cosTheta,
      passoAsse2: Math.min(a2Cross2 / sinTheta, 1.15 * a2Cross2),
      crossDominant: true,
    };
  }
  return {
    passoAsse1: skipElem1Spacing ? a2Cross2 / sinFold : Math.max(a1Own1, a2Cross2 / sinFold),
    passoAsse2: skipElem1Spacing ? a1Own2 : Math.max(a1Own2, a2Cross1 / sinFold),
    crossDominant: false,
  };
}
